import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from "vue"
import ManageTags from "@/modules/Missions/components/ManageTags/ManageTags.vue"
import Icon from "@/components/widgets/icons/Icon.vue"
import DocumentCard from "@/components/content/DocumentCard.vue"
import {
  MissionLifecycleFragmentFragment,
  UploadFileInput,
  SetTermsOfReferenceMutationMutation,
  SetTermsOfReferenceMutation,
  SetTermsOfReferenceMutationMutationVariables,
  MissionLifecycleState,
  ConsultantMissionLifecycle,
  ClientMissionLifecycleFragmentFragment,
} from "@/gql"
import { useUtilities } from "@/hooks/useUtilities"
import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import { RefetchQueryDescription } from "apollo-client/core/watchQueryOptions"
import { TagColor } from "@/components/widgets/common/TagChip.vue"
import CountrySelectV2 from "@/components/widgets/input/CountrySelectV2.vue"
import { useCountry } from "@/hooks/useCountry"
import { useAccountContext } from "@/hooks/useAccountContext"
import SecondaryButton from "@/components/widgets/button/SecondaryButton.vue"
import TextEditor from "@/components/widgets/input/TextEditor.vue"
import { useMissionLifecycleContext } from "@/modules/Missions/hooks/useMissionLifecycleContext"
import { UpdateMissionLifecycleInput } from "@/modules/Missions/types"
import ExpandableHtmlBlock from "@/components/widgets/common/ExpandableHtmlBlock.vue"

type MissionDetailsFormValues = {
  date: string | null
  country: string | null
  summary: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OverviewCard',
  props: {
    mission: null,
    refetchQueries: null
  },
  setup(__props: any) {

const props = __props as {
  mission:
    | MissionLifecycleFragmentFragment
    | ConsultantMissionLifecycle
    | ClientMissionLifecycleFragmentFragment
  refetchQueries: RefetchQueryDescription
}



const TORLoading = ref(false)
const updateCountryDialog = ref(false)

const { formatDate } = useUtilities()
const { mutate } = useMutation()
const { addSuccess } = useNotification()
const { getCountryByCode } = useCountry()
const { isStaff, isClient } = useAccountContext()

const { isLoading, updateMissionLifecycle } = useMissionLifecycleContext()

const uneditableStates = [
  MissionLifecycleState.ExecutionStage,
  MissionLifecycleState.Completed,
  MissionLifecycleState.Archived,
  MissionLifecycleState.Paused,
]

const termsOfReferenceEditable = computed(() => {
  return isStaff || isClient
})

const canEditDates = computed(() => {
  return isStaff && !uneditableStates.includes(props.mission.state as MissionLifecycleState)
})

const canEditSummary = computed(() => {
  return isStaff && props.mission.state !== MissionLifecycleState.Completed
})

const missionCompletion = computed(() => {
  return props.mission.completionProgress
})

const country = computed(() => {
  if (!props.mission.projectLocationCountryCode) return null
  return getCountryByCode(props.mission.projectLocationCountryCode)
})

const tagList = computed(() => {
  const colors: TagColor[] = [TagColor.Orange, TagColor.LimeGreen, TagColor.Purple]
  const mission = props.mission

  return [
    country.value?.en,
    mission.projectLocationType?.replace(/_/g, " "),
    mission.scopeService?.name,
    mission.scopeService?.serviceGroup.name,
    ...mission.tagList,
  ]
    .filter((tag): tag is string => !!tag)
    .map((tag, index) => ({
      text: tag,
      color: colors[index] || colors[0],
    }))
})

const missionCompletionDetails = computed(() => {
  const states = {
    initial: { text: "No activities", color: "grey" },
    inprogress: { text: "Ongoing", color: "#DFAB46" },
    completed: { text: "Completed", color: "green" },
  }

  if (missionCompletion.value === 0) {
    return states.initial
  } else if (missionCompletion.value > 0 && missionCompletion.value < 100) {
    return states.inprogress
  } else if (missionCompletion.value === 100) {
    return states.completed
  }

  return null
})

const onTORChange = async (doc: UploadFileInput[]) => {
  TORLoading.value = true

  const result = await mutate<
    SetTermsOfReferenceMutationMutation,
    SetTermsOfReferenceMutationMutationVariables
  >({
    mutation: SetTermsOfReferenceMutation,
    variables: {
      missionId: props.mission.prn,
      uploadFiles: doc,
    },
    refetchQueries: props.refetchQueries,
  })

  if (result?.data?.setTermsOfReference) {
    addSuccess("Terms Of Reference uploaded successfully!")
  }

  TORLoading.value = false
}

const form = ref<MissionDetailsFormValues>({
  date: null,
  country: country.value?.code ?? null,
  summary: props.mission.summary ?? "",
})

const onDateChangeStartDate = (date: string) => {
  form.value.date = date
  _updateMissionLifecycle({ tentativeStartDate: date })
}

const onDateChangeEndDate = (date: string) => {
  form.value.date = date
  _updateMissionLifecycle({ tentativeEndDate: date })
}

const minDate = computed(() => {
  return props.mission.startDate
    ? formatDate(props.mission.startDate, "YYYY-MM-DD")
    : formatDate(new Date(), "YYYY-MM-DD")
})

const maxDate = computed(() => {
  return props.mission.endDate ? formatDate(props.mission.endDate, "YYYY-MM-DD") : null
})

const attributeLoadingState = ref<{ [key: string]: boolean }>({})

const setDateOnCalendar = (date: string) => {
  form.value.date = formatDate(date, "YYYY-MM-DD")
}

const onCountryChange = () => {
  updateCountryDialog.value = false

  if (!form.value.country) return
  _updateMissionLifecycle({ projectLocationCountryCode: form.value.country })
}

const onUpdateSummary = async () => {
  _updateMissionLifecycle({ summary: form.value.summary })
}

const _updateMissionLifecycle = async (updateInput: UpdateMissionLifecycleInput) => {
  Object.keys(updateInput).forEach((field) => {
    attributeLoadingState.value[field] = true
  })

  await updateMissionLifecycle({
    updateInput,
    onDone: () => {
      Object.keys(updateInput).forEach((field) => {
        attributeLoadingState.value[field] = false
      })
    },
    onSuccess: () => {
      showEditModal.value = false
    },
  })
}

const showEditModal = ref(false)

return { __sfc: true,props, TORLoading, updateCountryDialog, formatDate, mutate, addSuccess, getCountryByCode, isStaff, isClient, isLoading, updateMissionLifecycle, uneditableStates, termsOfReferenceEditable, canEditDates, canEditSummary, missionCompletion, country, tagList, missionCompletionDetails, onTORChange, form, onDateChangeStartDate, onDateChangeEndDate, minDate, maxDate, attributeLoadingState, setDateOnCalendar, onCountryChange, onUpdateSummary, _updateMissionLifecycle, showEditModal, ManageTags, Icon, DocumentCard, CountrySelectV2, SecondaryButton, TextEditor, ExpandableHtmlBlock }
}

})